import type { Candle } from "@trader/shared/MarketData"
import { BigDecimal } from "effect"
import { CandlestickSeries, createChart, HistogramSeries, type MouseEventParams } from "lightweight-charts"
import { useEffect, useRef, useState } from "react"
import { call, errorField, errorTag } from "../../api.ts"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import { ContextMenu, type ContextMenuItem } from "../../ui/ContextMenu.tsx"
import { ChevronDownIcon, ChevronUpIcon } from "../../ui/icons.tsx"
import { toast } from "../../ui/toasts.ts"
import { DrawingFloatingToolbar } from "../drawings/DrawingFloatingToolbar.tsx"
import { DrawingSettingsDialog } from "../drawings/DrawingSettingsDialog.tsx"
import type { DrawingsStatus } from "../drawings/drawingsHub.ts"
import { PaneDrawings, type SelectedDrawing, type TextEditRequest } from "../drawings/PaneDrawings.ts"
import { TextEditor } from "../drawings/TextEditor.tsx"
import { IndicatorFloatingToolbar } from "../indicators/IndicatorFloatingToolbar.tsx"
import { IndicatorLayer, type LegendValue } from "../indicators/IndicatorLayer.ts"
import { IndicatorLegend } from "../indicators/IndicatorLegend.tsx"
import { useIndicatorModules } from "../indicators/indicatorModules.ts"
import { IndicatorSettingsDialog } from "../indicators/IndicatorSettingsDialog.tsx"
import { useInstrument } from "../instruments/useInstrument.ts"
import { PaneStrategy } from "../strategies/PaneStrategy.ts"
import { StrategyLegend } from "../strategies/StrategyLegend.tsx"
import { StrategySettingsDialog } from "../strategies/StrategySettingsDialog.tsx"
import { replaySession, useReplaySession } from "../replay/replaySession.ts"
import { ReplayBadge } from "../trading/ReplayBadge.tsx"
import { useSyncedRunner } from "../trading/useRunnerReport.ts"
import { ReplayCursor } from "../trading/ReplayCursor.ts"
import { TradingLayer } from "../trading/TradingLayer.ts"
import { snapPrice } from "../trading/chartOrders.ts"
import { formatPrice } from "../trading/format.ts"
import { messageFor } from "../trading/tradingApi.ts"
import { chartOrderChoices, decimalInput, startingQty, ticketBlockedReason, ticketInstrument } from "../trading/orderTicket.ts"
import { placeChartOrder } from "../trading/positionActions.ts"
import { QuickTrade } from "../trading/QuickTrade.tsx"
import { accountStore, chartQuotes, lastTicketQty, quickTradePreference, ticketRequests, useQuickTrade } from "../trading/tradingData.ts"
import {
  type IndicatorInstance,
  paneIndicators,
  type PaneState,
  removeIndicator,
  updateIndicator
} from "../workspace/workspaceStore.ts"
import { CandleFeed, type ChartSeries, type FeedProvenance, type FeedStatus } from "./CandleFeed.ts"
import { CANDLE_OPTIONS, CHART_OPTIONS, VOLUME_OPTIONS } from "./chartTheme.ts"
import { attachLegend } from "./legend.ts"
import { useLegendsFolded } from "./legendFold.ts"
import { syncNotice } from "./syncBars.ts"

const failureMessages: Record<string, string> = {
  ProviderNotConnected: "Connect Fyers to load this chart.",
  ProviderUnavailable: "Fyers is unavailable from the server right now.",
  OandaNotConnected: "OANDA isn't set up on the server: add OANDA_API_TOKEN to its backend.env.",
  OandaUnavailable: "OANDA is unavailable from the server right now.",
  NoProviderForVenue: "No data for this symbol and timeframe yet.",
  UnknownInstrument: "Not in the instrument catalog yet. It syncs daily from Fyers."
}

const statusMessage = (status: FeedStatus): string | null => {
  switch (status.kind) {
    case "loading":
      return "Loading…"
    case "empty":
      return "No data for this range."
    case "failed":
      return failureMessages[failureKey(status.error)] ?? "Couldn't load data."
    case "ready":
      return null
  }
}

/** The message key for a failure: provider errors are worded per provider (Fyers, OANDA). */
const failureKey = (error: unknown): string => {
  const tag = errorTag(error)
  return errorField(error, "provider") === "oanda" && tag.startsWith("Provider") ? tag.replace("Provider", "Oanda") : tag
}

/** Fetches the latest bars for the server's sync list now, then reloads the chart. */
function SyncButton({ onSynced }: { readonly onSynced: () => void }) {
  const [running, setRunning] = useState(false)
  const sync = () => {
    setRunning(true)
    call((client) => client.candles.sync())
      .then((response) => {
        const notice = syncNotice(response)
        toast(notice.tone, notice.title, notice.detail)
        if (!response.alreadyRunning) onSynced()
      }, () => toast("error", "Couldn't sync", "The server didn't answer; try again."))
      .finally(() => setRunning(false))
  }
  return (
    <button
      type="button"
      className="pane-sync"
      disabled={running}
      onClick={sync}
      title="Fetch the latest bars for the synced instruments (OANDA CFDs) now. The server also syncs every hour."
    >
      {running ? "Syncing…" : "Sync"}
    </button>
  )
}

const endDate = new Intl.DateTimeFormat("en-GB", { timeZone: DISPLAY_TIME_ZONE, day: "numeric", month: "short", year: "numeric" })

/** Which vendor the bars came from, so stopgap dataset bars are never mistaken for broker data. */
function ProvenanceBadge({ provenance }: { readonly provenance: FeedProvenance }) {
  if (provenance.sources.length === 0) return null
  const ends = provenance.endsAt === undefined ? "" : endDate.format(provenance.endsAt * 1000)
  const title = [
    `Bars from ${provenance.sources.join(", ")}.`,
    ends ? `Stored history ends ${ends}; recent bars need the market's broker.` : "",
    provenance.complete ? "" : "Some of this range isn't loaded: the market's broker isn't connected."
  ].filter(Boolean).join(" ")
  // The warning comes first, so a narrow pane cuts the vendor list rather than it.
  const warning = ends ? `to ${ends}` : provenance.complete ? "" : "partial"
  return (
    <span className={warning ? "pane-source pane-source-partial" : "pane-source"} title={title}>
      {warning ? `${warning} · ` : ""}
      {provenance.sources.join(" + ")}
    </span>
  )
}

interface ChartPaneProps {
  /** Position in the workspace, for indicator changes. */
  readonly index: number
  readonly pane: PaneState
  /** The pane the toolbars act on (armed drawing tool, new indicators). */
  readonly active: boolean
  /** Outline it as active (only when several charts are shown). */
  readonly highlighted: boolean
  readonly area: string
  readonly onActivate: () => void
}

export function ChartPane({ active, area, highlighted, index, onActivate, pane }: ChartPaneProps) {
  const instrument = useInstrument(pane.instrumentId)
  const indicators = paneIndicators(pane)
  const strategy = pane.strategy ?? null
  /** What the legends list: the strategy and each indicator (the fold button's count). */
  const legendCount = (strategy === null ? 0 : 1) + indicators.length
  // The renderer draws the strategy's own plots too.
  const modules = useIndicatorModules(indicators.length > 0 || strategy !== null)

  const containerRef = useRef<HTMLDivElement>(null)
  const legendRef = useRef<HTMLDivElement>(null)
  const [series, setSeries] = useState<ChartSeries | null>(null)
  const [status, setStatus] = useState<FeedStatus>({ kind: "loading" })
  const [provenance, setProvenance] = useState<FeedProvenance>({ sources: [], complete: true })

  // Controllers that outlive renders; the feed's bars are shared with drawings and indicators.
  const barsRef = useRef<ReadonlyArray<Candle>>([])
  // The same without a replay's forming bar: strategies and their backtests run on closed bars only.
  const closedBarsRef = useRef<ReadonlyArray<Candle>>([])
  const [drawings, setDrawings] = useState<PaneDrawings | null>(null)
  const [layer, setLayer] = useState<IndicatorLayer | null>(null)
  const layerRef = useRef<IndicatorLayer | null>(null)
  const [paneStrategy, setPaneStrategy] = useState<PaneStrategy | null>(null)
  const paneStrategyRef = useRef<PaneStrategy | null>(null)
  // The paper account's orders, positions and fills on this chart; the replay time it stops at.
  const feedRef = useRef<CandleFeed | null>(null)
  const tradingRef = useRef<TradingLayer | null>(null)
  /** The chart's right-click menu, where it was opened. */
  const [menu, setMenu] = useState<{ readonly x: number; readonly y: number; readonly price: BigDecimal.BigDecimal | null } | null>(null)
  const quickTrade = useQuickTrade()
  // TradingView's ^ under the legends: fold the strategy and indicator list away (per chart).
  const [legendsFolded, setLegendsFolded] = useLegendsFolded(index)

  /**
   * The menu's order items at the price under the pointer (TradingView's): a limit and a stop,
   * placed at once on the followed paper account, and "Create new order…" for the ticket.
   */
  const orderItems = (price: BigDecimal.BigDecimal | null): Array<ContextMenuItem> => {
    if (price === null || instrument.status !== "ready") return []
    const traded = instrument.instrument
    const state = accountStore.getState()
    const account = state?.summary ?? null
    const spec = ticketInstrument(traded)
    const at = formatPrice(price, spec.pricePrecision, spec.currency)
    const none = () => undefined
    if (state === null || account === null) return [{ label: "Create a paper account to trade from the chart", disabled: true, onSelect: none }]
    const blocked = ticketBlockedReason(account, traded)
    if (blocked !== null) return [{ label: blocked, disabled: true, onSelect: none }]
    const lastClose = chartQuotes.last(traded.id)
    const last = lastClose === null ? null : snapPrice(lastClose, spec.tickSize)
    const qtyText = startingQty(lastTicketQty.get(traded.id), spec.qtyStep, spec.lotSize)
    const qty = BigDecimal.fromStringUnsafe(qtyText)
    const held = state.positions.find((position) => position.instrumentId === traded.id && position.product === "Cash")?.qty ?? BigDecimal.fromBigInt(0n)
    const choices = chartOrderChoices(price, last)
    const items: Array<ContextMenuItem> = choices.map((choice) => ({
      label: `${choice.side} ${choice.type} ${qtyText}`,
      hint: at,
      icon: { text: choice.side === "Buy" ? "↑" : "↓", tone: choice.side === "Buy" ? "up" : "down" },
      onSelect: () => {
        placeChartOrder(account, traded, choice, qty, price, held).catch((error: unknown) => {
          const message = messageFor(error)
          toast("error", `Couldn't place it: ${message.title}`, message.detail)
        })
      }
    }))
    const first = choices[0]
    items.push({
      label: "Create new order…",
      hint: at,
      icon: { text: "+", tone: "muted" },
      onSelect: () =>
        ticketRequests.open({
          instrumentId: traded.id,
          side: first?.side ?? "Buy",
          type: first === undefined ? "Market" : "Limit",
          price: first === undefined ? null : decimalInput(price, spec.pricePrecision)
        })
    })
    return items
  }
  const cursorRef = useRef<ReplayCursor | null>(null)
  const [cursorKnown, setCursorKnown] = useState(false)

  const [textEdit, setTextEdit] = useState<TextEditRequest | null>(null)
  // The drawing whose settings dialog is open, as it was when the dialog opened (Cancel restores it).
  const [drawingSettings, setDrawingSettings] = useState<SelectedDrawing>(null)
  const [drawingsStatus, setDrawingsStatus] = useState<DrawingsStatus>("loading")
  const [settingsFor, setSettingsFor] = useState<IndicatorInstance | null>(null)
  const [strategySettingsOpen, setStrategySettingsOpen] = useState(false)
  // What a floating toolbar manages: a selected drawing, or a selected indicator (never both).
  const [selectedDrawing, setSelectedDrawing] = useState<SelectedDrawing>(null)
  const [selectedIndicator, setSelectedIndicator] = useState<string | null>(null)
  const [legendValues, setLegendValues] = useState<ReadonlyMap<string, ReadonlyArray<LegendValue>>>(new Map())

  // Indicators: a layer once the indicator code is loaded.
  useEffect(() => {
    if (series === null || modules === null) return
    const indicatorLayer = new IndicatorLayer(series.chart, series.candles, modules)
    // The bars it draws on; nothing is computed until the indicators are set.
    indicatorLayer.setBars(barsRef.current, false)
    layerRef.current = indicatorLayer
    setLayer(indicatorLayer)
    return () => {
      layerRef.current = null
      indicatorLayer.dispose()
    }
  }, [series, modules])

  // The strategy's backtests and trade marks, while the pane has one. Backtests
  // don't wait for the indicator code; it only draws the strategy's own plots.
  const hasStrategy = strategy !== null
  useEffect(() => {
    if (series === null || !hasStrategy) return
    const controller = new PaneStrategy(index, series.chart, series.candles)
    controller.setBars(closedBarsRef.current)
    paneStrategyRef.current = controller
    setPaneStrategy(controller)
    return () => {
      paneStrategyRef.current = null
      setPaneStrategy(null)
      controller.dispose()
    }
  }, [series, hasStrategy, index])
  useEffect(() => paneStrategy?.setModules(modules), [paneStrategy, modules])

  // Whether (and where) the chart stops at a replay account's virtual time. The
  // feed waits until that is known, so a reloaded page doesn't flash the present first.
  useEffect(() => {
    const cursor = new ReplayCursor((end) => feedRef.current?.setEnd(end), () => setCursorKnown(true))
    cursorRef.current = cursor
    if (cursor.isKnown()) setCursorKnown(true)
    return () => {
      cursorRef.current = null
      cursor.dispose()
    }
  }, [])

  // Working-order and average-price lines and fill marks of the followed paper account.
  useEffect(() => {
    if (series === null) return
    const trading = new TradingLayer(series.chart, series.candles, containerRef.current!)
    trading.setBars(barsRef.current)
    tradingRef.current = trading
    return () => {
      tradingRef.current = null
      trading.dispose()
    }
  }, [series])
  useEffect(() => tradingRef.current?.setInstrument(pane.instrumentId), [series, pane.instrumentId])
  useEffect(() => {
    if (instrument.status === "ready") tradingRef.current?.setInstrumentDetails(instrument.instrument)
  }, [series, pane.instrumentId, instrument])

  // A new feed whenever the symbol or timeframe changes.
  useEffect(() => {
    if (series === null || instrument.status !== "ready" || !cursorKnown) return
    layerRef.current?.setChart(instrument.instrument, pane.timeframe)
    const instrumentId = instrument.instrument.id
    const feed = new CandleFeed(series, instrument.instrument, pane.timeframe, {
      onStatus: setStatus,
      onProvenance: setProvenance,
      onBars: (bars, tailOnly, forming) => {
        barsRef.current = bars
        closedBarsRef.current = forming ? bars.slice(0, -1) : bars
        chartQuotes.report(instrumentId, bars.at(-1)?.close)
        tradingRef.current?.setBars(bars)
        paneStrategyRef.current?.setBars(closedBarsRef.current)
        const indicatorLayer = layerRef.current
        if (indicatorLayer === null) return
        indicatorLayer.setBars(bars, tailOnly)
        setLegendValues(indicatorLayer.valuesAt(null))
      }
    }, cursorRef.current?.current() ?? null)
    feedRef.current = feed
    const detachLegend = attachLegend(series, legendRef.current!, instrument.instrument)
    return () => {
      feedRef.current = null
      feed.dispose()
      detachLegend()
    }
  }, [series, instrument, pane.timeframe, cursorKnown])

  // Drawings follow the symbol and timeframe; tools arm only in the active pane.
  useEffect(() => {
    if (drawings !== null && instrument.status === "ready") drawings.show(instrument.instrument, pane.timeframe)
  }, [drawings, instrument, pane.timeframe])
  useEffect(() => {
    drawings?.setActive(active)
    // Selections belong to the active chart (Delete / Backspace act on them).
    if (!active) setSelectedIndicator(null)
  }, [drawings, active])

  // Indicator set or inputs changed.
  useEffect(() => {
    if (layer === null || instrument.status !== "ready") return
    layer.setChart(instrument.instrument, pane.timeframe)
    layer.setIndicators(indicators)
    setLegendValues(layer.valuesAt(null))
  }, [layer, instrument, pane.timeframe, JSON.stringify(indicators)])

  // Strategy, its settings, or the chart's series changed: rerun (hiding only redraws).
  // In a synced replay the backtest is the paper runner's own run: its inputs, capital and start (docs/design/0007).
  const syncedRunner = useSyncedRunner(strategy?.id, pane.instrumentId)
  const effectiveStrategy = strategy !== null && syncedRunner !== null
    ? { ...strategy, inputs: syncedRunner.inputs, properties: syncedRunner.properties }
    : strategy
  useEffect(() => {
    if (paneStrategy === null || instrument.status !== "ready") return
    paneStrategy.setStrategy(effectiveStrategy, instrument.instrument, pane.timeframe)
  }, [paneStrategy, instrument, pane.timeframe, JSON.stringify(effectiveStrategy)])

  // Clicking an indicator's line selects it (its toolbar opens); double-clicking opens its settings.
  useEffect(() => {
    if (series === null || layer === null) return
    const onClick = (param: MouseEventParams) => {
      const instance = layer.instanceOf(param.hoveredSeries)
      setSelectedIndicator(instance?.uid ?? null)
      if (instance !== undefined) drawings?.deselect()
    }
    const onDoubleClick = (param: MouseEventParams) => {
      const instance = layer.instanceOf(param.hoveredSeries)
      if (instance !== undefined) setSettingsFor(instance)
    }
    series.chart.subscribeClick(onClick)
    series.chart.subscribeDblClick(onDoubleClick)
    return () => {
      series.chart.unsubscribeClick(onClick)
      series.chart.unsubscribeDblClick(onDoubleClick)
    }
  }, [series, layer, drawings])

  // "Replay" (docs/design/0007): the bar clicked on any chart is where the replay starts.
  const replay = useReplaySession()
  const selectingReplay = replay.status === "picking"
  const strategyRef = useRef(strategy)
  strategyRef.current = strategy
  useEffect(() => {
    if (series === null || !selectingReplay || instrument.status !== "ready") return
    const at = instrument.instrument
    const onClick = (param: MouseEventParams) => {
      if (typeof param.time !== "number") return
      replaySession.picked({ instrument: at, timeframe: pane.timeframe, barTime: param.time, pane: index, strategy: strategyRef.current })
    }
    series.chart.subscribeClick(onClick)
    return () => series.chart.unsubscribeClick(onClick)
  }, [series, selectingReplay, instrument, pane.timeframe, index])

  // Legend values follow the crosshair, at most once per frame.
  useEffect(() => {
    if (series === null || layer === null) return
    let frame = 0
    let latest: MouseEventParams | null = null
    const onMove = (param: MouseEventParams) => {
      latest = param
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        setLegendValues(layer.valuesAt(latest?.time === undefined ? null : latest))
      })
    }
    series.chart.subscribeCrosshairMove(onMove)
    return () => {
      cancelAnimationFrame(frame)
      series.chart.unsubscribeCrosshairMove(onMove)
    }
  }, [series, layer])

  // One chart per pane, for the pane's lifetime, with its drawings. Declared last
  // on purpose: React runs effect cleanups in declaration order, so the feed,
  // legend, indicator layer, and strategy all detach while the chart still exists
  // (removing a pane with an indicator crashed when the chart went first).
  useEffect(() => {
    const chart = createChart(containerRef.current!, CHART_OPTIONS)
    const candles = chart.addSeries(CandlestickSeries, CANDLE_OPTIONS)
    const volume = chart.addSeries(HistogramSeries, VOLUME_OPTIONS)
    chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } })
    const paneDrawings = new PaneDrawings(
      chart,
      candles,
      () => barsRef.current,
      setTextEdit,
      (selected) => {
        setSelectedDrawing(selected)
        if (selected !== null) setSelectedIndicator(null)
      },
      setDrawingsStatus,
      setDrawingSettings
    )
    setSeries({ chart, candles, volume })
    setDrawings(paneDrawings)
    return () => {
      paneDrawings.dispose()
      chart.remove()
    }
  }, [])

  const message = instrument.status === "failed"
    ? failureMessages[errorTag(instrument.error)] ?? "Couldn't load symbol."
    : statusMessage(status)
  const settingsEntry = settingsFor === null ? undefined : modules?.byId.get(settingsFor.id)
  const selectedInstance = indicators.find((instance) => instance.uid === selectedIndicator)
  const selectedEntry = selectedInstance === undefined ? undefined : modules?.byId.get(selectedInstance.id)

  return (
    // Capture phase: the drawing library stops a press on a drawing from bubbling,
    // and the pressed chart must become active before it handles the press.
    <section className={highlighted ? "pane pane-active" : "pane"} style={{ gridArea: area }} onPointerDownCapture={onActivate}>
      <header className="pane-header">
        <span className="pane-symbol">{pane.instrumentId}</span>
        <span className="pane-timeframe">{pane.timeframe}</span>
        <ReplayBadge />
        {instrument.status === "ready" && (
          <span className="pane-name">
            {instrument.instrument.name} · {instrument.instrument.currency}
          </span>
        )}
        <span ref={legendRef} className="pane-legend" />
        {drawingsStatus === "unavailable" && (
          <span
            className="pane-source pane-source-partial"
            title="This symbol's drawings couldn't be loaded; retrying. They're not saved until they load, so nothing on the server is overwritten."
          >
            drawings: retrying
          </span>
        )}
        {drawingsStatus === "unsaved" && (
          <span
            className="pane-source pane-source-partial"
            title="The server refused to save this symbol's drawings (too large, or the session ended). They're kept here and saved with the next change."
          >
            drawings: not saved
          </span>
        )}
        <ProvenanceBadge provenance={provenance} />
        <SyncButton onSynced={() => feedRef.current?.reload()} />
      </header>
      {selectedDrawing && drawings && (
        <DrawingFloatingToolbar
          drawing={selectedDrawing}
          drawings={drawings}
          onOpenSettings={() => setDrawingSettings(drawings.current(selectedDrawing.id))}
        />
      )}
      {selectedInstance && selectedEntry && (
        <IndicatorFloatingToolbar
          entry={selectedEntry}
          instance={selectedInstance}
          onOpenSettings={() => setSettingsFor(selectedInstance)}
          onToggle={() => updateIndicator(selectedInstance.uid, { hidden: !selectedInstance.hidden })}
          onRemove={() => {
            removeIndicator(selectedInstance.uid)
            setSelectedIndicator(null)
          }}
          onClose={() => setSelectedIndicator(null)}
        />
      )}
      <div className="pane-chart-area">
      {/* The chart's top-left: the Buy / Sell buttons, then the strategy and indicator legends under them. */}
      <div className="pane-corner">
        {quickTrade && instrument.status === "ready" && <QuickTrade instrument={instrument.instrument} />}
        <div className="pane-legends">
          {!legendsFolded && (
            <>
              {strategy && <StrategyLegend pane={index} instance={strategy} onOpenSettings={() => setStrategySettingsOpen(true)} />}
              {modules !== null && indicators.length > 0 && (
                <IndicatorLegend
                  instances={indicators}
                  catalog={modules.byId}
                  values={legendValues}
                  errors={layer?.errors ?? new Map()}
                  selected={selectedIndicator}
                  onSelect={(instance) => {
                    setSelectedIndicator(instance.uid)
                    drawings?.deselect()
                  }}
                  onToggle={(instance) => updateIndicator(instance.uid, { hidden: !instance.hidden })}
                  onOpenSettings={setSettingsFor}
                  onRemove={(instance) => removeIndicator(instance.uid)}
                />
              )}
            </>
          )}
          {legendCount > 0 && (
            <button
              type="button"
              className="legend-fold"
              aria-expanded={!legendsFolded}
              aria-label={legendsFolded ? `Show the indicator list (${legendCount})` : "Hide the indicator list"}
              title={legendsFolded ? "Show the indicator list" : "Hide the indicator list"}
              onClick={() => setLegendsFolded(!legendsFolded)}
            >
              {legendsFolded ? <ChevronDownIcon /> : <ChevronUpIcon />}
              {legendsFolded && <span className="legend-fold-count">{legendCount}</span>}
            </button>
          )}
        </div>
      </div>
      <div
        ref={containerRef}
        className="pane-chart"
        onContextMenu={(event) => {
          // A drawing (or the chart) that handles its own right-click keeps it.
          if (event.defaultPrevented) return
          event.preventDefault()
          // The price under the pointer, on the instrument's tick grid.
          const top = containerRef.current?.getBoundingClientRect().top ?? 0
          const raw = series?.candles.coordinateToPrice(event.clientY - top) ?? null
          const price = raw === null || instrument.status !== "ready" ? null : snapPrice(raw, ticketInstrument(instrument.instrument).tickSize)
          setMenu({ x: event.clientX, y: event.clientY, price })
        }}
      />
      </div>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            ...orderItems(menu.price),
            {
              separated: menu.price !== null && instrument.status === "ready",
              label: "Refresh",
              hint: "Reload the bars",
              onSelect: () => {
                // The chart's bars (as stored now), and the followed account's orders and fills.
                feedRef.current?.reload()
                accountStore.refreshHistory()
              }
            },
            {
              label: quickTrade ? "Hide Buy / Sell buttons" : "Show Buy / Sell buttons",
              onSelect: () => quickTradePreference.set(quickTrade ? "off" : "on")
            }
          ]}
        />
      )}
      {selectingReplay && <div className="replay-hint">Click a bar to start the replay from it · Esc cancels</div>}
      {message && <div className="pane-message">{message}</div>}
      {textEdit && drawings && (
        <TextEditor
          // The editor sits in the pane, the chart below its header.
          request={{
            ...textEdit,
            at: {
              x: textEdit.at.x + (containerRef.current?.offsetLeft ?? 0),
              y: textEdit.at.y + (containerRef.current?.offsetTop ?? 0)
            }
          }}
          onDone={(text) => {
            drawings.setText(textEdit.drawing, text)
            setTextEdit(null)
          }}
          onCancel={() => setTextEdit(null)}
        />
      )}
      {drawingSettings && drawings && (
        <DrawingSettingsDialog
          initial={drawingSettings}
          title={drawingSettings.name ?? drawingSettings.kind.replace(/-/g, " ")}
          onChange={(drawing) => drawings.replace(drawing)}
          onClose={() => setDrawingSettings(null)}
        />
      )}
      {settingsFor && settingsEntry && (
        <IndicatorSettingsDialog
          entry={settingsEntry}
          instance={settingsFor}
          onApply={(patch) => updateIndicator(settingsFor.uid, patch)}
          onPreview={(style) => {
            layer?.preview(settingsFor.uid, style)
            if (layer) setLegendValues(layer.valuesAt(null))
          }}
          onClose={() => setSettingsFor(null)}
        />
      )}
      {strategySettingsOpen && strategy && instrument.status === "ready" && (
        <StrategySettingsDialog
          pane={index}
          instance={strategy}
          symbolCurrency={instrument.instrument.currency}
          onClose={() => setStrategySettingsOpen(false)}
        />
      )}
    </section>
  )
}
